import { Button, Checkbox, Form, Modal, Spin, Table, Tag } from 'antd';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CarOutlined, FileTextOutlined, MessageOutlined, SafetyOutlined, SolutionOutlined } from '@ant-design/icons';
import { Assets } from '../assets';
import { MYCDate, MYCInput, MYCRemoteSearchSelect, MYCSelect, MYCTextArea } from './input_form.component';
import { Constants, TRIP_STATUSES, TripAssignStatuses, TripStatus, TripType, isRegionalManagerRole, getRegionalOriginIds } from '../constants/general.constant';
import { Endpoints, getResourceUrl } from '../constants/endpoint.constant';
import { useForm } from 'antd/es/form/Form';
import { useGet, useGetPaginated, usePost } from '../hooks/useRest.hook';
import { SuccessModal } from './success_modal.component';
import { MYCTable } from './table.component';
import { pdf } from '@react-pdf/renderer';
import { CancelDoc, CompareDocument, CompleteDoc, InspectionDoc, TripOrderDoc } from '../documents/triporder.doc';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { TrackingComplianceSection } from './tracking_compliance_section.component';
import { TripSlipsPanel } from './trip_slips_panel.component';
import { httpClient } from '../api/httpClient';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { prepareTripPdfPayload } from '../utils/pdf_image.util';
import { formatInspectionDate, formatInspectionDateTime, formatInspectionTime, normalizeInspectionChoiceState, normalizeInspectionRecord } from '../utils/inspection.util';
import { getTripReference } from '../utils/tripReference.util';
import { formatDateTimeZA, formatDateZA, formatTimeZA } from '../utils/date.util';
import { UserContext } from '../App';

const FieldView = ({ label, value, height, children }) => {
    return <div className="flex flex-col gap-3 ">
        <span>{label}</span>
        <div style={{ height }} className="border border-solid self-stretch text-center px-5 py-3">
            {value || children}
        </div>
    </div>
}
const ChoiceView = ({ label, yes, yesLabel = "Yes", noLabel = "No" }) => {
    const choice = normalizeInspectionChoiceState(yes);

    return <div className="flex flex-col gap-3 items-center">
        <span>{label}</span>
        <div className="border flex flex-row justify-between gap-10  border-solid px-10 py-5 ">
            <div className="flex items-center flex-row gap-2">
                <span>{yesLabel}</span>
                <div className={`${choice === true ? "bg-primary-orange" : "border border-solid"}  w-5 h-5 `}></div>
            </div>
            <div className="flex items-center flex-row gap-2">
                <span>{noLabel}</span>
                <div className={`${choice === false ? "bg-primary-orange" : "border border-solid"}  w-5 h-5 `}></div>
            </div>
        </div>
    </div>
}

const InspectionImage = ({ resource, className = "", style, onClick, alt = "" }) => {
    const [failed, setFailed] = useState(false);
    const src = getResourceUrl(resource);

    if (!src || failed) {
        return <span className="self-center text-sm text-[#8c7b6b]">No image</span>;
    }

    return <img
        alt={alt}
        onClick={onClick}
        onError={() => setFailed(true)}
        src={src}
        style={style}
        className={className}
    />;
}

const ActionLoadingIcon = () => (
    <span className="inline-flex h-6 w-6 items-center justify-center">
        <span className="h-4 w-4 rounded-full border-2 border-[#d65f3d] border-t-[#f2d4c7]" />
    </span>
);

const DownloadProgressBar = ({ progress, className = "" }) => {
    if (!progress) return null;

    const progressPercent = Math.max(0, Math.min(100, Number(progress?.percent) || 0));

    return <div className={`w-full max-w-[220px] ${className}`}>
        <div className="h-2 overflow-hidden rounded-full bg-[#f1e6df]">
            <div
                className="h-full rounded-full bg-primary-orange transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
            />
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-[#6b5b4d]">
            <span className="truncate">{progress?.phase || "Preparing download"}</span>
            <span>{progressPercent}%</span>
        </div>
    </div>
}

const SummaryCell = ({ label, value, valueClassName = "", mono = false }) => {
    const displayValue = value === null || value === undefined || value === "" ? "Not available" : value;
    return <div className="min-w-0 flex flex-col gap-2 rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">{label}</span>
        <span className={`min-w-0 text-[1.05rem] leading-7 text-[#161311] whitespace-normal break-words ${mono ? "font-mono text-[0.95rem]" : ""} ${valueClassName}`}>
            {displayValue}
        </span>
    </div>
}

const formatTripAddress = (value) => {
    if (!value) return "Address unavailable";

    const trimmedValue = value.toString().trim();
    if (!trimmedValue) return "Address unavailable";

    const looksLikeOpaqueToken =
        trimmedValue.length > 40 &&
        !/\s|,/.test(trimmedValue) &&
        /[A-Z]/.test(trimmedValue) &&
        /\d/.test(trimmedValue);

    return looksLikeOpaqueToken ? "Address unavailable" : trimmedValue;
}

const getCancellationEntry = trip => Array.isArray(trip?.cancelledTrips) && trip.cancelledTrips.length
    ? trip.cancelledTrips[trip.cancelledTrips.length - 1]
    : null;

const formatCancellationUser = entry => {
    const user = entry?.createdByUser;
    const fullName = [user?.name, user?.surname].filter(Boolean).join(" ").trim();
    return fullName || user?.email || entry?.createdBy || "Not available";
};

const formatCancellationDate = entry => {
    const value = entry?.createdAt;
    if (!value) return "";

    const millis =
        typeof value?.toMillis === "function"
            ? value.toMillis()
            : typeof value?._seconds === "number"
                ? value._seconds * 1000
                : typeof value === "number"
                    ? value
                    : Date.parse(value);

    return Number.isFinite(millis) ? formatDateTimeZA(millis) : "";
};

const buildOfferDeliverySummary = (messages, loading) => {
    if (loading && !messages?.length) {
        return {
            statusLabel: "Checking...",
            statusClassName: "text-[#8c7b6b]",
            recipientsLabel: "Loading delivery history",
            canViewRecipients: false,
        };
    }

    if (!messages?.length) {
        return {
            statusLabel: "Offer not sent yet",
            statusClassName: "text-red-500 font-medium",
            recipientsLabel: "No recipients yet",
            canViewRecipients: false,
        };
    }

    const targetedCount = messages.filter((message) => !!message?.driverId).length;
    const broadcastCount = messages.filter((message) => message?.driverId == null).length;
    const latestMessage = [...messages].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0];
    const latestSentAt = latestMessage?.createdAt ? formatDateTimeZA(latestMessage.createdAt) : null;

    let statusLabel = "Offer sent";
    if (broadcastCount && targetedCount) {
        statusLabel = `Sent (${broadcastCount} broadcast${broadcastCount > 1 ? "s" : ""}, ${targetedCount} direct)`;
    } else if (broadcastCount) {
        statusLabel = broadcastCount === 1 ? "Broadcast to region" : `Broadcast ${broadcastCount} times`;
    } else if (targetedCount) {
        statusLabel = targetedCount === 1 ? "Sent to 1 driver" : `Sent to ${targetedCount} drivers`;
    }

    return {
        statusLabel,
        statusClassName: "text-green-600 font-medium",
        recipientsLabel: latestSentAt ? `Last sent ${latestSentAt}` : "Recipients available",
        canViewRecipients: true,
    };
}

const resolveDriverNameFromTripMessage = (message) => {
    const user = message?.driver?.user || {};
    const name = [user?.name, user?.surname || user?.lastName].filter(Boolean).join(" ").trim();
    return name || message?.driverName || message?.driver?.name || message?.driverId || "";
}

const buildSentToDriverLabel = (messages = [], trip = {}) => {
    const tripNames = Array.isArray(trip?.offeredDriverNames) ? trip.offeredDriverNames : [];
    const names = [
        ...new Set(
            [
                ...tripNames,
                ...(messages || [])
                    .filter((message) => !!message?.driverId)
                    .map(resolveDriverNameFromTripMessage),
            ]
                .filter(Boolean)
        ),
    ];

    if (!names.length && trip?.offeredToAllDrivers) return " all drivers in region";
    if (!names.length) return "";
    if (names.length === 1) return ` driver ${names[0]}`;
    if (names.length === 2) return ` drivers ${names.join(" and ")}`;
    return ` drivers ${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
}

const SectionCard = ({ title, children, className = "" }) => {
    return <div className={`rounded-[28px] border border-[#ece5dd] bg-white px-8 py-7 shadow-[0_18px_55px_rgba(15,23,42,0.05)] ${className}`}>
        {title ? <span className="self-start text-[1.15rem] font-semibold text-[#161311]">{title}</span> : null}
        <div className={title ? "mt-6" : ""}>
            {children}
        </div>
    </div>
}

const SectionBanner = ({ title, icon, className = "" }) => {
    return <div className={`inline-flex self-start items-center gap-3 rounded-2xl bg-primary-orange px-4 py-2 text-white shadow-[0_16px_40px_rgba(236,88,43,0.24)] ${className}`}>
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/15 text-base">
            {icon}
        </span>
        <span className="text-sm font-semibold uppercase tracking-[0.12em]">{title}</span>
    </div>
}

const DetailSection = ({ title, icon, children, className = "" }) => {
    return <div className={`flex flex-col gap-4 ${className}`}>
        <div className="inline-flex self-start items-center gap-3 rounded-2xl bg-primary-orange px-4 py-2 text-white">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/15 text-base">
                {icon}
            </span>
            <span className="text-sm font-semibold uppercase tracking-[0.12em]">{title}</span>
        </div>
        {children}
    </div>
}

const VehicleReviewBlock = ({ setImgModal, label, windscreen, image, dent, scratch, comment }) => {
    return <div className="flex flex-col gap-5">
        <div className="bg-primary-orange py-5 text-white text-center">
            {label}
        </div>
        <div className="grid grid-cols-2 gap-5">
            {image?.map?.((M, index) => <InspectionImage key={`${label}-${index}`} alt={label} onClick={setImgModal?.bind(this, M?.pic)} resource={M?.pic} className="object-contain w-full h-32" />)}
        </div>
        {!windscreen && <><ChoiceView label="Dent" yes={dent} />
            <ChoiceView label="Scratch" yes={scratch} />
        </>}
        <FieldView label="Comments" value={comment} />
    </div>
}


const InspectComponent = ({ inspect, setImgModal, trip }) => {
    const inspection = normalizeInspectionRecord(inspect, trip);

    return <div className="gap-5 flex flex-col px-3">
        <FieldView label="Client Reference Number" value={inspection?.refNumber || "Not available"} />
        <FieldView label="Registration Number" value={inspection?.regsitrationNumber || "Not available"} />
        <FieldView label="Make" value={inspection?.make || "Not available"} />
        <FieldView label="Model" value={inspection?.model || "Not available"} />
        <FieldView label="Colour" value={inspection?.colour || "Not available"} />
        <FieldView label="Date Out" value={formatInspectionDate(inspection?.dateOut)} />
        <FieldView label="KMs Out" value={inspection?.kmsOut || "Not available"} />
        <FieldView label="Odometer">
            <InspectionImage alt="Odometer" onClick={setImgModal.bind(this, inspection?.odometer)} resource={inspection?.odometer} className="h-64 self-center object-contain" />
        </FieldView>
        <FieldView label="Licence disk">
            <InspectionImage alt="Licence disk" onClick={setImgModal.bind(this, inspection?.licenseDiskImage)} resource={inspection?.licenseDiskImage} className="h-64 self-center object-contain" />
        </FieldView>
        <FieldView label="Time Out" value={formatInspectionTime(inspection?.timeOut)} />
        <FieldView label="From (source)" value={inspection?.source || "Not available"} />
        <FieldView label="To (destination)" value={inspection?.destination || "Not available"} />
        <ChoiceView label="Fuel" yes={inspection?.fuel} />
        <FieldView label="Type" value={inspection?.type || "Not available"} />
        <FieldView label="Driver" value={inspection?.driver?.user?.name || inspection?.driverName || "Not available"} />
        <span className="self-center text-xl">Vehicle Inspection</span>
        <VehicleReviewBlock setImgModal={setImgModal} label="Left Side" dent={inspection?.leftDent} scratch={inspection?.leftScratch} comment={inspection?.leftCommnet} image={inspection?.leftPics} />
        <VehicleReviewBlock setImgModal={setImgModal} label="Right Side" dent={inspection?.rightDent} scratch={inspection?.rightScratch} comment={inspection?.rightComment} image={inspection?.rightPics} />
        <VehicleReviewBlock setImgModal={setImgModal} label="Car Front" dent={inspection?.frontDent} scratch={inspection?.frontScratch} comment={inspection?.frontComment} image={inspection?.frontPics} />
        <VehicleReviewBlock label="Car Rear" setImgModal={setImgModal} dent={inspection?.backDent} scratch={inspection?.backScratch} comment={inspection?.backComment} image={inspection?.backPics} />
        <VehicleReviewBlock label="Windscreen" windscreen dent={false} setImgModal={setImgModal} scratch={false} comment={inspection?.windScreenComment} image={inspection?.windScreenPics} />
        <span className="self-center text-xl">Tyre Inspection</span>

        <TyreReviewBlock setImgModal={setImgModal} label="Left Front Tyre" tread={inspection?.leftFrontTireTread} pressure={inspection?.leftFrontTirePressure} image={inspection?.leftFrontTirePics} make={inspection?.leftFrontTireMake} />
        <TyreReviewBlock setImgModal={setImgModal} label="Left Back Tyre" tread={inspection?.leftBackTireTread} pressure={inspection?.leftBackTirePressure} image={inspection?.leftBackTirePics} make={inspection?.leftBackTireMake} />
        <TyreReviewBlock setImgModal={setImgModal} label="Right Back Tyre" tread={inspection?.rightFrontTireTread} pressure={inspection?.rightFrontTirePressure} image={inspection?.rightFrontTirePics} make={inspection?.rightFrontTireMake} />
        <TyreReviewBlock setImgModal={setImgModal} label="Right Front Tyre" tread={inspection?.rightBackTireTread} pressure={inspection?.rightBackTirePressure} image={inspection?.rightBackTirePics} make={inspection?.rightBackTireMake} />
        <span className="self-center text-xl">Sign Off</span>

        <ChoiceView label="E Tag" yes={inspection?.etag} />
        <ChoiceView label="Tools & Jack" yes={inspection?.toolsAndJack} />
        <ChoiceView label="Spare Wheel" yes={inspection?.spareWheel} />
        <ChoiceView label="Removal Hubcaps" yes={inspection?.removableHubcaps} />
        <ChoiceView label="Triangle" yes={inspection?.triangle} />
        <ChoiceView label="Parcel Tray" yes={inspection?.parcelTray} />
        <ChoiceView label="Trailer" yes={inspection?.trailer} />
        <ChoiceView label="Tow Bar" yes={inspection?.towBar} />
        <FieldView label="Date" value={formatInspectionDateTime(inspection?.createdAt)} />
        <FieldView label="Driver Signature">
            <InspectionImage alt="Driver Signature" resource={inspection?.driverSignature} className="w-64 h-32 self-center object-contain" />
        </FieldView>
        <FieldView label="Company name">{inspection?.companyName || "Not available"}</FieldView>
        <FieldView label="Region">{inspection?.region || "Not available"}</FieldView>
        <FieldView label="Dispatcher - name">{inspection?.personName || "Not available"}</FieldView>
        <FieldView label="Mobile number">{inspection?.number || "Not available"}</FieldView>
        <FieldView label="Client Signature">
            <InspectionImage alt="Client Signature" resource={inspection?.signature} className="w-64 h-32  self-center object-contain" />
        </FieldView>
    </div>
}


const CompareInspectModal = ({ inspect, postInspect, open, onCancel, title, tripOrderId, trip }) => {
    const [imgModal, setImgModal] = useState(null);
    const preInspection = normalizeInspectionRecord(inspect, trip);
    const postInspection = normalizeInspectionRecord(postInspect, trip, preInspection);
    return <> <Modal width={900} footer={null} open={open} title={title} onCancel={onCancel}>
        <div className="grid grid-cols-2  gap-5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <FieldView label="Client Reference Number" value={preInspection?.refNumber || "Not available"} />
            <FieldView label="Client Reference Number" value={postInspection?.refNumber || "Not available"} />
            <FieldView label="Registration Number" value={preInspection?.regsitrationNumber || "Not available"} />
            <FieldView label="Registration Number" value={postInspection?.regsitrationNumber || "Not available"} />
            <FieldView label="Make" value={preInspection?.make || "Not available"} />
            <FieldView label="Make" value={postInspection?.make || "Not available"} />
            <FieldView label="Model" value={preInspection?.model || "Not available"} />
            <FieldView label="Model" value={postInspection?.model || "Not available"} />
            <FieldView label="Colour" value={preInspection?.colour || "Not available"} />
            <FieldView label="Colour" value={postInspection?.colour || "Not available"} />
            <FieldView label="Date Out" value={formatInspectionDate(preInspection?.dateOut)} />
            <FieldView label="Date Out" value={formatInspectionDate(postInspection?.dateOut)} />
            <FieldView label="KMs Out" value={preInspection?.kmsOut || "Not available"} />
            <FieldView label="KMs Out" value={postInspection?.kmsOut || "Not available"} />
            <FieldView label="Odometer">
                <InspectionImage alt="Pre odometer" onClick={setImgModal.bind(this, preInspection?.odometer)} resource={preInspection?.odometer} className="h-64 self-center object-contain" />
            </FieldView>
            <FieldView label="Odometer">
                <InspectionImage alt="Post odometer" onClick={setImgModal.bind(this, postInspection?.odometer)} resource={postInspection?.odometer} className="h-64 self-center object-contain" />
            </FieldView>
            <FieldView label="Licence disk">
                <InspectionImage alt="Pre licence disk" onClick={setImgModal.bind(this, preInspection?.licenseDiskImage)} resource={preInspection?.licenseDiskImage} className="h-64 self-center object-contain" />
            </FieldView>
            <FieldView label="Licence disk">
                <InspectionImage alt="Post licence disk" onClick={setImgModal.bind(this, postInspection?.licenseDiskImage)} resource={postInspection?.licenseDiskImage} className="h-64 self-center object-contain" />
            </FieldView>
            <FieldView label="Time Out" value={formatInspectionTime(preInspection?.timeOut)} />
            <FieldView label="Time Out" value={formatInspectionTime(postInspection?.timeOut)} />

            <FieldView label="From (source)" value={preInspection?.source || "Not available"} />
            <FieldView label="From (source)" value={postInspection?.source || "Not available"} />
            <FieldView label="To (destination)" value={preInspection?.destination || "Not available"} />
            <FieldView label="To (destination)" value={postInspection?.destination || "Not available"} />
            <ChoiceView label="Fuel" yes={preInspection?.fuel} />
            <ChoiceView label="Fuel" yes={postInspection?.fuel} />
            <FieldView label="Type" value={preInspection?.type || "Not available"} />
            <FieldView label="Type" value={postInspection?.type || "Not available"} />
            <FieldView label="Driver" value={preInspection?.driver?.user?.name || preInspection?.driverName || "Not available"} />
            <FieldView label="Driver" value={postInspection?.driver?.user?.name || postInspection?.driverName || "Not available"} />
            <span className="self-center text-xl col-span-2 text-center">Vehicle Inspection</span>
            <VehicleReviewBlock setImgModal={setImgModal} label="Left Side" dent={preInspection?.leftDent} scratch={preInspection?.leftScratch} comment={preInspection?.leftCommnet} image={preInspection?.leftPics} />
            <VehicleReviewBlock setImgModal={setImgModal} label="Left Side" dent={postInspection?.leftDent} scratch={postInspection?.leftScratch} comment={postInspection?.leftCommnet} image={postInspection?.leftPics} />

            <VehicleReviewBlock setImgModal={setImgModal} label="Right Side" dent={preInspection?.rightDent} scratch={preInspection?.rightScratch} comment={preInspection?.rightComment} image={preInspection?.rightPics} />
            <VehicleReviewBlock setImgModal={setImgModal} label="Right Side" dent={postInspection?.rightDent} scratch={postInspection?.rightScratch} comment={postInspection?.rightComment} image={postInspection?.rightPics} />

            <VehicleReviewBlock setImgModal={setImgModal} label="Car Front" dent={preInspection?.frontDent} scratch={preInspection?.frontScratch} comment={preInspection?.frontComment} image={preInspection?.frontPics} />
            <VehicleReviewBlock setImgModal={setImgModal} label="Car Front" dent={postInspection?.frontDent} scratch={postInspection?.frontScratch} comment={postInspection?.frontComment} image={postInspection?.frontPics} />

            <VehicleReviewBlock label="Car Rear" setImgModal={setImgModal} dent={preInspection?.backDent} scratch={preInspection?.backScratch} comment={preInspection?.backComment} image={preInspection?.backPics} />
            <VehicleReviewBlock label="Car Rear" setImgModal={setImgModal} dent={postInspection?.backDent} scratch={postInspection?.backScratch} comment={postInspection?.backComment} image={postInspection?.backPics} />

            <VehicleReviewBlock label="Windscreen" windscreen dent={false} setImgModal={setImgModal} scratch={false} comment={preInspection?.windScreenComment} image={preInspection?.windScreenPics} />
            <VehicleReviewBlock label="Windscreen" windscreen dent={false} setImgModal={setImgModal} scratch={false} comment={postInspection?.windScreenComment} image={postInspection?.windScreenPics} />

            <span className="self-center text-xl col-span-2 text-center">Tyre Inspection</span>

            <TyreReviewBlock setImgModal={setImgModal} label="Left Front Tyre" tread={preInspection?.leftFrontTireTread} pressure={preInspection?.leftFrontTirePressure} image={preInspection?.leftFrontTirePics} make={preInspection?.leftFrontTireMake} />
            <TyreReviewBlock setImgModal={setImgModal} label="Left Front Tyre" tread={postInspection?.leftFrontTireTread} pressure={postInspection?.leftFrontTirePressure} image={postInspection?.leftFrontTirePics} make={postInspection?.leftFrontTireMake} />

            <TyreReviewBlock setImgModal={setImgModal} label="Left Back Tyre" tread={preInspection?.leftBackTireTread} pressure={preInspection?.leftBackTirePressure} image={preInspection?.leftBackTirePics} make={preInspection?.leftBackTireMake} />
            <TyreReviewBlock setImgModal={setImgModal} label="Left Back Tyre" tread={postInspection?.leftBackTireTread} pressure={postInspection?.leftBackTirePressure} image={postInspection?.leftBackTirePics} make={postInspection?.leftBackTireMake} />

            <TyreReviewBlock setImgModal={setImgModal} label="Right Back Tyre" tread={preInspection?.rightFrontTireTread} pressure={preInspection?.rightFrontTirePressure} image={preInspection?.rightFrontTirePics} make={preInspection?.rightFrontTireMake} />
            <TyreReviewBlock setImgModal={setImgModal} label="Right Back Tyre" tread={postInspection?.rightFrontTireTread} pressure={postInspection?.rightFrontTirePressure} image={postInspection?.rightFrontTirePics} make={postInspection?.rightFrontTireMake} />

            <TyreReviewBlock setImgModal={setImgModal} label="Right Front Tyre" tread={preInspection?.rightBackTireTread} pressure={preInspection?.rightBackTirePressure} image={preInspection?.rightBackTirePics} make={preInspection?.rightBackTireMake} />
            <TyreReviewBlock setImgModal={setImgModal} label="Right Front Tyre" tread={postInspection?.rightBackTireTread} pressure={postInspection?.rightBackTirePressure} image={postInspection?.rightBackTirePics} make={postInspection?.rightBackTireMake} />
            <span className="self-center text-xl text-center col-span-2">Sign Off</span>

            <ChoiceView label="E Tag" yes={preInspection?.etag} />
            <ChoiceView label="E Tag" yes={postInspection?.etag} />

            <ChoiceView label="Tools & Jack" yes={preInspection?.toolsAndJack} />
            <ChoiceView label="Tools & Jack" yes={postInspection?.toolsAndJack} />

            <ChoiceView label="Spare Wheel" yes={preInspection?.spareWheel} />
            <ChoiceView label="Spare Wheel" yes={postInspection?.spareWheel} />

            <ChoiceView label="Removal Hubcaps" yes={preInspection?.removableHubcaps} />
            <ChoiceView label="Removal Hubcaps" yes={postInspection?.removableHubcaps} />

            <ChoiceView label="Triangle" yes={preInspection?.triangle} />
            <ChoiceView label="Triangle" yes={postInspection?.triangle} />

            <ChoiceView label="Parcel Tray" yes={preInspection?.parcelTray} />
            <ChoiceView label="Parcel Tray" yes={postInspection?.parcelTray} />

            <ChoiceView label="Trailer" yes={preInspection?.trailer} />
            <ChoiceView label="Trailer" yes={postInspection?.trailer} />

            <ChoiceView label="Tow Bar" yes={preInspection?.towBar} />
            <ChoiceView label="Tow Bar" yes={postInspection?.towBar} />

            <FieldView label="Date" value={formatInspectionDateTime(preInspection?.createdAt)} />
            <FieldView label="Date" value={formatInspectionDateTime(postInspection?.createdAt)} />

            <FieldView label="Driver Signature">

                <InspectionImage alt="Pre driver signature" resource={preInspection?.driverSignature} className="w-64 h-32 self-center object-contain" />
            </FieldView>
            <FieldView label="Driver Signature">

                <InspectionImage alt="Post driver signature" resource={postInspection?.driverSignature} className="w-64 h-32 self-center object-contain" />
            </FieldView>

            <FieldView label="Company name">{preInspection?.companyName || "Not available"}</FieldView>
            <FieldView label="Company name">{postInspection?.companyName || "Not available"}</FieldView>

            <FieldView label="Region">{preInspection?.region || "Not available"}</FieldView>
            <FieldView label="Region">{postInspection?.region || "Not available"}</FieldView>

            <FieldView label="Dispatcher - name">{preInspection?.personName || "Not available"}</FieldView>
            <FieldView label="Dispatcher - name">{postInspection?.personName || "Not available"}</FieldView>

            <FieldView label="Mobile number">{preInspection?.number || "Not available"}</FieldView>
            <FieldView label="Mobile number">{postInspection?.number || "Not available"}</FieldView>

            <FieldView label="Client Signature">
                <InspectionImage alt="Pre client signature" resource={preInspection?.signature} className="w-64 h-32  self-center object-contain" />
            </FieldView>
            <FieldView label="Client Signature">
                <InspectionImage alt="Post client signature" resource={postInspection?.signature} className="w-64 h-32  self-center object-contain" />
            </FieldView>
        </div>
        <div className="w-full mt-5 flex flex-row justify-end">
            <Button type="primary" onClick={onCancel}>Close</Button>
        </div>
    </Modal>
        <Modal width={700} footer={null} className="flex flex-col items-center" open={imgModal} title="Image View" onCancel={setImgModal.bind(this, false)}>
            <img alt="" src={getResourceUrl(imgModal)} style={{ width: 500, height: 500 }} className="object-contain" />
        </Modal>
    </>
}

const InspectionModal = ({ inspect, open, onCancel, title, tripOrderId, postInspect, trip }) => {
    const [imgModal, setImgModal] = useState(null);
    return <> <Modal footer={null} open={open} title={title} onCancel={onCancel}>
        <InspectComponent inspect={inspect} setImgModal={setImgModal} trip={trip} />
        <div className="w-full mt-5 flex flex-row justify-end">
            <Button type="primary" onClick={onCancel}>Close</Button>
        </div>
    </Modal>
        <Modal width={700} footer={null} className="flex flex-col items-center" open={imgModal} title="Image View" onCancel={setImgModal.bind(this, false)}>

            <img alt="" src={getResourceUrl(imgModal)} style={{ width: 500, height: 500 }} className="object-contain" />
        </Modal>
    </>
}

const TyreReviewBlock = ({ setImgModal, label, image, tread, pressure, make }) => {
    return <div className="flex flex-col gap-5">
        <div className="bg-primary-orange py-5 text-white text-center">
            {label}
        </div>
        <div className="grid grid-cols-2 gap-5">
            {image?.map?.((M, index) => <InspectionImage key={`${label}-${index}`} alt={label} onClick={setImgModal?.bind(this, M?.pic)} resource={M?.pic} className="object-contain w-full h-32" />)}
        </div>
        <ChoiceView label="Tread" yesLabel="Good" noLabel="Bad" yes={tread} />
        <ChoiceView label="Pressure" yes={pressure} />
        <FieldView label="Make" value={make} />
    </div>
}

const generateCompareDocument = async ({ data, fileName, onProgress }) => {
    onProgress?.({ percent: 2, phase: "Loading compliance evidence" });
    const preparedData = await prepareTripPdfPayload(data, {
        onProgress: progress => onProgress?.({
            ...progress,
            percent: Math.min(90, Math.max(5, Math.round((Number(progress?.percent) || 0) * 0.85) + 5)),
        }),
    });
    onProgress?.({ percent: 94, phase: "Building PDF" });
    const blob = await pdf(<CompareDocument {...preparedData} />).toBlob();
    onProgress?.({ percent: 100, phase: "Starting download" });
    saveAs(blob, fileName);
}

const loadTripCompliance = async tripId => {
    if (!tripId) return null;

    try {
        const response = await httpClient.get(Endpoints.GET_TRIP_COMPLIANCE(tripId));
        return response?.data?.success ? response.data.data : null;
    } catch (e) {
        return null;
    }
}

const prepareDownloadPayload = async (data, onProgress) => {
    onProgress?.({ percent: 3, phase: "Loading evidence" });
    const preparedData = await prepareTripPdfPayload(data, {
        onProgress: progress => onProgress?.({
            ...progress,
            percent: Math.min(90, Math.max(5, Math.round((Number(progress?.percent) || 0) * 0.85) + 5)),
        }),
    });
    onProgress?.({ percent: 94, phase: "Building PDF" });
    return preparedData;
}

const completeDownload = (blob, fileName, onProgress) => {
    onProgress?.({ percent: 100, phase: "Starting download" });
    saveAs(blob, fileName);
}

const generatePdfDocument = async ({ data, fileName, onProgress }) => {
    const preparedData = await prepareDownloadPayload(data, onProgress);
    const blob = await pdf(<CompleteDoc {...preparedData} />).toBlob();
    completeDownload(blob, fileName, onProgress);
}

const generateInspectionDocument = async ({ data, fileName, onProgress }) => {
    const preparedData = await prepareDownloadPayload(data, onProgress);
    const blob = await pdf(<InspectionDoc {...preparedData} />).toBlob();
    completeDownload(blob, fileName, onProgress);
}

const generateCancelDocument = async ({ data, fileName, onProgress }) => {
    const preparedData = await prepareDownloadPayload(data, onProgress);
    const blob = await pdf(<CancelDoc {...preparedData} />).toBlob();
    completeDownload(blob, fileName, onProgress);
}

const generateTripOrderDocument = async ({ data, fileName, onProgress }) => {
    const preparedData = await prepareDownloadPayload(data, onProgress);
    const blob = await pdf(<TripOrderDoc {...preparedData} />).toBlob();
    completeDownload(blob, fileName, onProgress);
}

const formatEvidenceDateTime = value => {
    return formatDateTimeZA(value);
}

const formatEvidenceSpeed = value => {
    const number = Number(value);
    return Number.isFinite(number) ? `${number.toFixed(1)} km/h` : 'Not available';
}

const formatEvidenceDuration = value => {
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return 'Not available';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

const formatEvidenceStatus = value => {
    if (value === 'confirmed') return 'Red infringement';
    if (value === 'inconclusive') return 'Yellow warning';
    if (!value) return 'None';
    return value
        .toString()
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

const evidenceSeverityColor = value => {
    if (value === 'success' || value === 'resolved') return 'green';
    if (value === 'warning' || value === 'inconclusive' || value === 'recovering') return 'orange';
    if (value === 'error' || value === 'confirmed' || value === 'ended_without_recovery') return 'red';
    return 'default';
}

const buildTripEvidencePackText = ({ trip, driverName, driverIdentifier, compliance }) => {
    const summary = compliance?.summary || {};
    const alerts = compliance?.alerts || [];
    const speeding = compliance?.infringements || [];
    const offline = compliance?.offlineInfringements || [];
    const timeline = compliance?.timeline || [];
    const provider = compliance?.provider || {};

    const sections = [
        'MOVE YOUR CARS - TRIP COMPLIANCE EVIDENCE PACK',
        `Trip: MYC #${trip?.id}`,
        `Client Ref: ${getTripReference(trip) || 'Not available'}`,
        `Vehicle Reg: ${trip?.vehicleRegNumber || trip?.VehicleRegNo || 'Not available'}`,
        `Driver: ${driverName || 'Not available'}`,
        `Driver Identifier: ${driverIdentifier || 'Not available'}`,
        `Trip Status: ${TRIP_STATUSES[trip?.status] || 'Not available'}`,
        `Pickup: ${formatTripAddress(trip?.pickUp)}`,
        `Dropoff: ${formatTripAddress(trip?.dropOff)}`,
        `Generated At: ${formatDateTimeZA(Date.now())}`,
        '',
        'SUMMARY',
        `Tracking Status: ${summary?.trackingStatus?.label || 'Not available'}`,
        `Current Speed: ${formatEvidenceSpeed(summary?.currentSpeedKph)}`,
        `Rolling Average Speed: ${formatEvidenceSpeed(summary?.rollingAverageSpeedKph)}`,
        `Last Heartbeat: ${formatEvidenceDateTime(summary?.lastHeartbeatAt)}`,
        `Expected Heartbeat By: ${formatEvidenceDateTime(summary?.expectedHeartbeatBy)}`,
        `Speeding Events: ${summary?.speedingEventCount ?? 0}`,
        `Offline Infringements: ${summary?.offlineInfringementCount ?? 0}`,
        `Provider: ${provider?.name || 'driver-tracking-telemetry'}`,
        `Provider Status: ${provider?.status || 'unknown'}`,
        '',
        'ALERTS',
        alerts.length
            ? alerts.map((alert, index) => `${index + 1}. ${alert?.label || 'Alert'} | ${formatEvidenceDateTime(alert?.at)}`).join('\n')
            : 'No alerts logged.',
        '',
        'SPEEDING INFRINGEMENTS',
        speeding.length
            ? speeding.map((event, index) => [
                `${index + 1}. ${formatEvidenceStatus(event?.status)}`,
                `When: ${formatEvidenceDateTime(event?.startTime)} -> ${formatEvidenceDateTime(event?.endTime)}`,
                `Road: ${event?.roadName || event?.roadAddress || 'Road lookup pending'}`,
                `Driver Speed: ${formatEvidenceSpeed(event?.maxSpeedKph)}`,
                `Road Limit: ${formatEvidenceSpeed(event?.roadSpeedLimitKph)}`,
                `Over Limit: ${formatEvidenceSpeed(event?.amountOverLimitKph)?.replace(' km/h', '') === 'Not available' ? 'Not available' : `+${Number(event?.amountOverLimitKph || 0).toFixed(1)} km/h`}`,
                `Duration: ${formatEvidenceDuration(event?.durationOverLimitSeconds)}`,
                `Coordinates: ${event?.peakCoordinates || 'Not available'}`,
                `Geo Pin: ${event?.peakMapsLink || 'Not available'}`,
                `Samples: ${event?.sampleCountUsed ?? 0}`,
                `Confidence: ${Number(event?.confidence || 0).toFixed(2)}`,
                '',
            ].join('\n')).join('\n')
            : 'No speeding infringements logged.',
        '',
        'OFFLINE INFRINGEMENTS',
        offline.length
            ? offline.map((event, index) => [
                `${index + 1}. ${formatEvidenceStatus(event?.status)}`,
                `Start: ${formatEvidenceDateTime(event?.startTime)}`,
                `Checkpoint: ${formatEvidenceDateTime(event?.checkpointTime)}`,
                `End: ${formatEvidenceDateTime(event?.endTime)}`,
                `Duration: ${formatEvidenceDuration(event?.outageDurationSeconds)}`,
                `Interval: ${event?.intervalMinutes ?? 0} minutes`,
                `Classification: ${formatEvidenceStatus(event?.classification || event?.outageStatus)}`,
                `Coordinates: ${event?.startCoordinates || 'Not available'}`,
                `Geo Pin: ${event?.startMapsLink || 'Not available'}`,
                '',
            ].join('\n')).join('\n')
            : 'No offline infringements logged.',
        '',
        'TIMELINE',
        timeline.length
            ? timeline.map((event, index) => `${index + 1}. ${formatEvidenceDateTime(event?.at)} | ${event?.title || 'Event'}${event?.details ? ` | ${event.details}` : ''}`).join('\n')
            : 'No timeline records logged.',
        '',
    ];

    return sections.join('\n');
}

const TripComplianceEvidenceActions = ({ trip, driverName, driverIdentifier }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [compliance, setCompliance] = useState(null);

    const loadComplianceEvidence = async () => {
        if (!trip?.id) return null;
        setLoading(true);
        try {
            const response = await httpClient.get(Endpoints.GET_TRIP_COMPLIANCE(trip.id));
            if (!response?.data?.success) {
                throw new Error(response?.data?.message || 'Failed to load trip compliance evidence');
            }
            setCompliance(response.data.data);
            return response.data.data;
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || 'Failed to load trip compliance evidence');
            return null;
        } finally {
            setLoading(false);
        }
    }

    const handleViewEvidence = async () => {
        const payload = compliance || await loadComplianceEvidence();
        if (payload) {
            setOpen(true);
        }
    }

    const handleDownloadEvidence = async () => {
        const payload = compliance || await loadComplianceEvidence();
        if (!payload) return;

        const blob = new Blob(
            [buildTripEvidencePackText({ trip, driverName, driverIdentifier, compliance: payload })],
            { type: 'text/plain;charset=utf-8' }
        );
        saveAs(blob, `MYC-${trip?.id}-compliance-evidence.txt`);
    }

    const summary = compliance?.summary || {};
    const speeding = compliance?.infringements || [];
    const offline = compliance?.offlineInfringements || [];
    const timeline = compliance?.timeline || [];
    const alerts = compliance?.alerts || [];

    return <>
        <SectionCard title="Compliance Evidence Pack">
            <div className="flex flex-col gap-5">
                <span className="text-[#6e6258]">
                    Completed trips retain their speeding and offline infringement records here for review and download as a trip-level evidence pack.
                </span>
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                    <SummaryCell label="Speeding Logs" value={summary?.speedingEventCount ?? 0} />
                    <SummaryCell label="Offline Logs" value={summary?.offlineInfringementCount ?? 0} />
                    <SummaryCell label="Last Heartbeat" value={formatEvidenceDateTime(summary?.lastHeartbeatAt)} />
                    <SummaryCell label="Tracking Status" value={summary?.trackingStatus?.label || 'Not available'} />
                </div>
                <div className="flex flex-row gap-6">
                    <div className="flex flex-col gap-3">
                        <span className="font-bold">View Trip Logs</span>
                        <div className="flex flex-row gap-2">
                            {loading ? <ActionLoadingIcon /> : <img alt="" src={Assets.EyeSquare} onClick={handleViewEvidence} className="cursor-pointer w-6 h-6" />}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <span className="font-bold">Download Trip Logs</span>
                        <div className="flex flex-row gap-2">
                            {loading ? <ActionLoadingIcon /> : <img alt="" src={Assets.DownloadSquare} onClick={handleDownloadEvidence} className="cursor-pointer w-6 h-6" />}
                        </div>
                    </div>
                </div>
            </div>
        </SectionCard>

        <Modal
            width={1100}
            footer={null}
            open={open}
            title={`Trip Compliance Evidence - MYC #${trip?.id}`}
            onCancel={setOpen.bind(this, false)}
        >
            {loading && !compliance ? <div className="py-10 flex justify-center"><Spin /></div> : <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                    <SummaryCell label="Driver" value={driverName || 'Not available'} />
                    <SummaryCell label="Driver Identifier" value={driverIdentifier || 'Not available'} />
                    <SummaryCell label="Speeding Logs" value={speeding.length} />
                    <SummaryCell label="Offline Logs" value={offline.length} />
                </div>

                {alerts.length > 0 && <div className="flex flex-row flex-wrap gap-3">
                    {alerts.map((alert, index) => <Tag key={`${alert?.type || 'alert'}-${index}`} color={evidenceSeverityColor(alert?.severity)} className="rounded-full px-4 py-1">
                        {alert?.label || 'Alert'}
                    </Tag>)}
                </div>}

                <div className="flex flex-col gap-3">
                    <span className="font-bold text-[#161311]">Speeding Infringement Log</span>
                    <Table
                        size="small"
                        rowKey={record => record?.id || `speed-${record?.startTime || ''}`}
                        pagination={false}
                        dataSource={speeding}
                        locale={{ emptyText: 'No speeding infringements logged.' }}
                        scroll={{ x: 900 }}
                        columns={[
                            {
                                title: 'Status',
                                dataIndex: 'status',
                                render: text => <Tag color={evidenceSeverityColor(text)}>{formatEvidenceStatus(text)}</Tag>,
                            },
                            {
                                title: 'Road',
                                render: (_, record) => record?.roadName || record?.roadAddress || 'Road lookup pending',
                            },
                            {
                                title: 'Speed',
                                dataIndex: 'maxSpeedKph',
                                render: value => formatEvidenceSpeed(value),
                            },
                            {
                                title: 'Limit',
                                dataIndex: 'roadSpeedLimitKph',
                                render: value => formatEvidenceSpeed(value),
                            },
                            {
                                title: 'Over',
                                dataIndex: 'amountOverLimitKph',
                                render: value => Number.isFinite(Number(value)) ? `+${Number(value).toFixed(1)} km/h` : 'Not available',
                            },
                            {
                                title: 'When',
                                dataIndex: 'startTime',
                                render: value => formatEvidenceDateTime(value),
                            },
                            {
                                title: 'Duration',
                                dataIndex: 'durationOverLimitSeconds',
                                render: value => formatEvidenceDuration(value),
                            },
                            {
                                title: 'Geo Pin',
                                render: (_, record) => record?.peakMapsLink ? <a href={record.peakMapsLink} target="_blank" rel="noreferrer" className="text-primary-orange underline">Open Pin</a> : 'Unavailable',
                            },
                        ]}
                    />
                </div>

                <div className="flex flex-col gap-3">
                    <span className="font-bold text-[#161311]">Offline Infringement Log</span>
                    <Table
                        size="small"
                        rowKey={record => record?.id || `offline-${record?.checkpointTime || ''}`}
                        pagination={false}
                        dataSource={offline}
                        locale={{ emptyText: 'No offline infringements logged.' }}
                        scroll={{ x: 900 }}
                        columns={[
                            {
                                title: 'Status',
                                dataIndex: 'status',
                                render: text => <Tag color={evidenceSeverityColor(text)}>{formatEvidenceStatus(text)}</Tag>,
                            },
                            {
                                title: 'Start',
                                dataIndex: 'startTime',
                                render: value => formatEvidenceDateTime(value),
                            },
                            {
                                title: 'Checkpoint',
                                dataIndex: 'checkpointTime',
                                render: value => formatEvidenceDateTime(value),
                            },
                            {
                                title: 'Duration',
                                dataIndex: 'outageDurationSeconds',
                                render: value => formatEvidenceDuration(value),
                            },
                            {
                                title: 'Classification',
                                render: (_, record) => formatEvidenceStatus(record?.classification || record?.outageStatus),
                            },
                            {
                                title: 'Geo Pin',
                                render: (_, record) => record?.startMapsLink ? <a href={record.startMapsLink} target="_blank" rel="noreferrer" className="text-primary-orange underline">Open Pin</a> : 'Unavailable',
                            },
                        ]}
                    />
                </div>

                <div className="flex flex-col gap-3">
                    <span className="font-bold text-[#161311]">Trip Timeline</span>
                    <Table
                        size="small"
                        rowKey={record => record?.id || `timeline-${record?.at || ''}`}
                        pagination={false}
                        dataSource={timeline}
                        locale={{ emptyText: 'No timeline records logged.' }}
                        columns={[
                            {
                                title: 'When',
                                dataIndex: 'at',
                                render: value => formatEvidenceDateTime(value),
                            },
                            {
                                title: 'Event',
                                dataIndex: 'title',
                            },
                            {
                                title: 'Details',
                                dataIndex: 'details',
                                render: value => value || 'Not available',
                            },
                        ]}
                    />
                </div>
            </div>}
        </Modal>
    </>
}

const InspectionButton = ({ rq, trip, post = false }) => {
    const [inspect, setInspect] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(null);
    const hasInspection = !!rq?.data?.id;
    const isLoading = rq?.loading && !hasInspection;

    const handleView = () => {
        if (!hasInspection) {
            toast.error(`${post ? "Post" : "Pre"} inspection is not available yet`);
            return;
        }
        setInspect(true);
    }

    const handleDownload = async () => {
        if (!hasInspection) {
            toast.error(`${post ? "Post" : "Pre"} inspection is not available yet`);
            return;
        }

        if (downloading) return;

        setDownloading(true);
        setDownloadProgress({ percent: 1, phase: "Starting download" });

        try {
            await generateInspectionDocument({
                data: { inspection: rq?.data, trip, post },
                fileName: `MYC-${trip?.id}-${post ? "post" : "pre"}-inspection.pdf`,
                onProgress: setDownloadProgress,
            });
        } catch (e) {
            toast.error(e?.message || 'Failed to generate inspection PDF');
        } finally {
            setDownloading(false);
            setTimeout(() => setDownloadProgress(null), 900);
        }
    }


    return <div className="flex flex-col gap-2 min-w-[130px]"> <span className="font-bold">
        {post ? "Post" : "Pre"}-Inspection
    </span>
        <InspectionModal tripOrderId={trip?.id} inspect={rq?.data} trip={trip} open={inspect} title={`${post ? "Post" : "Pre"} Inspection Checklist`} onCancel={setInspect.bind(this, false)} />

        {isLoading
            ? <div className="flex h-6 flex-row items-center gap-2"><ActionLoadingIcon /></div>
            : <div className="flex h-6 flex-row items-center gap-2">
                <img alt="" src={Assets.EyeSquare} onClick={handleView} className={`${hasInspection ? "cursor-pointer" : "cursor-not-allowed opacity-40"} w-6 h-6`} />
                {downloading
                    ? <ActionLoadingIcon />
                    : <img alt="" src={Assets.DownloadSquare} onClick={handleDownload} className={`${hasInspection ? "cursor-pointer" : "cursor-not-allowed opacity-40"} w-6 h-6`} />}
            </div>}
        <DownloadProgressBar progress={downloadProgress} />
        {!hasInspection && <span className="text-xs text-gray-500">{isLoading ? "Loading..." : "Not available"}</span>}
    </div>
}

const CompareButton = ({ rq, postRq, trip, post = false }) => {
    const [inspect, setInspect] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(null);
    const hasPreInspection = !!rq?.data?.id;
    const hasPostInspection = !!postRq?.data?.id;
    const canCompare = hasPreInspection && hasPostInspection;
    const isLoading = (rq?.loading && !hasPreInspection) || (postRq?.loading && !hasPostInspection);

    const handleView = () => {
        if (!canCompare) {
            toast.error('Pre and post inspections are both required before comparing');
            return;
        }
        setInspect(true);
    }

    const handleDownload = async () => {
        if (!canCompare) {
            toast.error('Pre and post inspections are both required before comparing');
            return;
        }

        if (downloading) return;

        setDownloading(true);
        setDownloadProgress({ percent: 1, phase: "Starting download" });

        try {
            setDownloadProgress({ percent: 3, phase: "Loading compliance evidence" });
            const compliance = await loadTripCompliance(trip?.id);
            await generateCompareDocument({
                data: { trip, inspect: rq?.data, postInspect: postRq?.data, compliance },
                fileName: `MYC-${trip?.id}-comparision.pdf`,
                onProgress: setDownloadProgress,
            });
        } catch (e) {
            toast.error(e?.message || 'Failed to generate comparison PDF');
        } finally {
            setDownloading(false);
            setTimeout(() => setDownloadProgress(null), 900);
        }
    }

    return <div className="flex flex-col gap-2 min-w-[210px]"> <span className="font-bold">
        Compare pre and post inspection
    </span>
        <CompareInspectModal tripOrderId={trip?.id} inspect={rq?.data} postInspect={postRq?.data} trip={trip} open={inspect} title={`Compare Inspection Checklist`} onCancel={setInspect.bind(this, false)} />

        {isLoading
            ? <div className="flex h-6 flex-row items-center gap-2"><ActionLoadingIcon /></div>
            : <div className="flex h-6 flex-row items-center gap-2">
                <img alt="" src={Assets.EyeSquare} onClick={handleView} className={`${canCompare ? "cursor-pointer" : "cursor-not-allowed opacity-40"} w-6 h-6`} />
                <button
                    type="button"
                    aria-label="Download comparison PDF"
                    disabled={!canCompare || downloading}
                    onClick={handleDownload}
                    className="border-0 bg-transparent p-0 leading-none disabled:cursor-not-allowed"
                >
                    <img
                        alt=""
                        src={Assets.DownloadSquare}
                        className={`${canCompare && !downloading ? "cursor-pointer" : "cursor-not-allowed opacity-40"} w-6 h-6`}
                    />
                </button>
                {downloading ? <ActionLoadingIcon /> : null}
            </div>}
        <DownloadProgressBar progress={downloadProgress} />
        {!canCompare && <span className="text-xs text-gray-500">{isLoading ? "Loading..." : "Not available"}</span>}
    </div>
}

const TripEditModal = ({ open, onCancel, onSubmit, loading, trip }) => {
    const [form] = useForm();

    useEffect(() => {
        if (!open) return;

        form.setFieldsValue({
            ClientRefNo: getTripReference(trip),
            VehicleRegNo: trip?.vehicleRegNumber || trip?.VehicleRegNo || "",
            VehicleType: trip?.vehicleType || trip?.VehicleType || "",
            StartDateTime: trip?.startDateTime ? dayjs(trip.startDateTime) : null,
            ReturnDateTime: trip?.returnDateTime ? dayjs(trip.returnDateTime) : null,
            PickUp: trip?.pickUp || "",
            DropOff: trip?.dropOff || "",
        });
    }, [form, open, trip]);

    return <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        title={`Edit Trip Order MYC#${trip?.id}`}
        width={720}
    >
        <Form form={form} onFinish={onSubmit} layout="vertical">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MYCInput name="ClientRefNo" label="MYC Reference Number" disabled type="text" />
                <MYCInput name="VehicleRegNo" label="Vehicle Reg Number" type="text" />
                <MYCInput name="VehicleType" label="Vehicle Type" type="text" />
                <MYCDate name="StartDateTime" label="Start Date & Time" required />
                {trip?.type === TripType.Return && (
                    <MYCDate showTime={false} name="ReturnDateTime" label="Return Date" required />
                )}
                <div className="md:col-span-2">
                    <MYCInput name="PickUp" label="Pickup Address" type="text" />
                </div>
                <div className="md:col-span-2">
                    <MYCInput name="DropOff" label="Dropoff Address" type="text" />
                </div>
            </div>
            <span className="mt-4 block text-sm text-[#8c7b6b]">
                Editing addresses updates the trip text shown in the CMS. Recreate the trip if the route coordinates also need to change.
            </span>
            <div className="mt-6 flex justify-end gap-3">
                <Button onClick={onCancel}>Cancel</Button>
                <Button htmlType="submit" type="primary" loading={loading}>Save Changes</Button>
            </div>
        </Form>
    </Modal>
}

export const TripOrderCard = ({ trip, refresh, origins, driver }) => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [successModal, setSuccessModal] = useState(false);
    const [rcvsModal, setRcvsModal] = useState(false);
    const [documentLoadingKey, setDocumentLoadingKey] = useState(null);
    const [documentDownloadProgress, setDocumentDownloadProgress] = useState(null);
    const requestedInspectionTripRef = useRef({ pre: null, post: null });

    const driverReRq = usePost(Endpoints.ASSIGN_DRIVER(trip?.id, null))
    const driverMsgRq = usePost(Endpoints.DRIVER_MSG);
    const cancelRq = usePost(Endpoints.CANCEL_TRIP_ORDER(trip?.id));
    const updateTripRq = usePost(Endpoints.UPDATE_ORDER);
    const deleteTripRq = usePost(Endpoints.DELETE_TRIP_ORDER(trip?.id));
    const tripAssignHistoryQuery = useMemo(() => ({
        tripOrderHistoryId: trip?.id
    }), [trip?.id]);
    const tripAssignHistories = useGet(Endpoints.TRIP_ASSIGN_HISTORIES, false, tripAssignHistoryQuery);
    const preRq = useGet((Endpoints.GET_PRE_INS(trip?.id)), false);
    const postRq = useGet((Endpoints.GET_POST_INS(trip?.id)), false);


    const tripOrderMessages = useGetPaginated(Endpoints.GET_TRIPORDER_MESSAGES(trip?.id), false);
    const loadTripAssignHistories = tripAssignHistories?.load;
    const loadPreInspection = preRq?.load;
    const loadPostInspection = postRq?.load;
    const loadTripOrderMessages = tripOrderMessages?.load;
    const preInspectionId = preRq?.data?.id;
    const preInspectionLoading = preRq?.loading;
    const postInspectionId = postRq?.data?.id;
    const postInspectionLoading = postRq?.loading;
    const tripOrderMessageCount = tripOrderMessages?.list?.length;
    const tripOrderMessagesLoading = tripOrderMessages?.loading;

    const [driverReassignForm] = useForm();
    const [driverMsgForm] = useForm();
    const [cancelForm] = useForm();
    const originId = Form.useWatch('originId', driverMsgForm);
    const allDrivers = Form.useWatch('allDrivers', driverMsgForm);
    const isRegionalManager = isRegionalManagerRole(user);
    const isAdminUser = user?.role === Constants.ADMIN || user?.roles?.includes?.(Constants.ADMIN);
    const regionalOriginIds = getRegionalOriginIds(user);
    const tripOriginId = trip?.originId ? String(trip.originId) : "";
    const regionalDefaultOriginId = regionalOriginIds.includes(tripOriginId) ? tripOriginId : (regionalOriginIds[0] || tripOriginId);
    const driverMessageOriginId = isRegionalManager ? (originId || regionalDefaultOriginId) : originId;
    const driverSearchParams = new URLSearchParams();
    if (driverMessageOriginId) driverSearchParams.set('originId', driverMessageOriginId);
    if (isRegionalManager) driverSearchParams.set('regionalOnboarding', 'true');
    const driverSearchQuery = driverSearchParams.toString();
    const driverSearchUrl = Endpoints.ALL_DRIVERS + (driverSearchQuery ? `?${driverSearchQuery}` : '');
    const canCancelTrip = (isAdminUser || isRegionalManager) && trip?.status !== TripStatus.Completed && trip?.status !== TripStatus.Cancelled;
    const canManageDriverWorkflow = isAdminUser || isRegionalManager;
    const canUseAdminOnlyTripActions = isAdminUser && !isRegionalManager;
    const canTrackVehicle = isAdminUser || isRegionalManager;

    useEffect(() => {
        if (!isRegionalManager || !regionalDefaultOriginId) return;
        const currentOriginId = driverMsgForm.getFieldValue('originId');
        if (!currentOriginId) {
            driverMsgForm.setFieldsValue({ originId: regionalDefaultOriginId });
        }
    }, [driverMsgForm, isRegionalManager, regionalDefaultOriginId]);

    useEffect(() => {
        if (expanded) {
            loadTripAssignHistories?.();
            if (!preInspectionId && !preInspectionLoading && trip?.status !== TripStatus.Cancelled && requestedInspectionTripRef.current.pre !== trip?.id) {
                requestedInspectionTripRef.current.pre = trip?.id;
                loadPreInspection?.();
            }
            if (!postInspectionId && !postInspectionLoading && trip?.status !== TripStatus.Cancelled && requestedInspectionTripRef.current.post !== trip?.id) {
                requestedInspectionTripRef.current.post = trip?.id;
                loadPostInspection?.();
            }
            if (isAdminUser || isRegionalManager) {
                loadTripOrderMessages?.();
            }

        }
    }, [
        expanded,
        isAdminUser,
        isRegionalManager,
        loadPostInspection,
        loadPreInspection,
        loadTripAssignHistories,
        loadTripOrderMessages,
        postInspectionId,
        postInspectionLoading,
        preInspectionId,
        preInspectionLoading,
        trip?.id,
        trip?.status,
    ])

    useEffect(() => {
        if (trip?.status !== TripStatus.Pending) return;
        if (trip?.offeredDriverNames?.length || tripOrderMessageCount || tripOrderMessagesLoading) return;
        if (isAdminUser || isRegionalManager) {
            loadTripOrderMessages?.();
        }
    }, [trip?.id, trip?.status, trip?.offeredDriverNames?.length, tripOrderMessageCount, tripOrderMessagesLoading, isAdminUser, isRegionalManager, loadTripOrderMessages])



    const _driverMsg = async values => {
        values.tripOrderId = trip?.id;
        if (isRegionalManager) values.originId = values.originId || regionalDefaultOriginId;
        values.driverIds = values.driverIds || [];
        try {
            await driverMsgRq.post(values);
            setSuccessModal("Trip order sent to driver(s) successfully");
            refresh();
            tripOrderMessages?.load?.();
        } catch (e) {

        }
    }

    const _driverReassign = async values => {
        try {
            await driverReRq?.put({}, Endpoints.ASSIGN_DRIVER(trip?.id, values?.driverId));
            setSuccessModal("Driver reassigned successfully");
            tripAssignHistories?.load?.();
            refresh();
        } catch (e) {

        }
    }
    const _cancelTripOrder = async values => {
        try {
            await cancelRq?.post(values);
            setCancelModal(false);
            setSuccessModal("Trip order cancelled successfully");
            refresh();
        }
        catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to cancel trip order");
        }
    }
    const _editTripOrder = async values => {
        const payload = [{
            TripOrderId: trip?.id,
            ClientRefNo: getTripReference(trip),
            VehicleRegNo: values?.VehicleRegNo || "",
            VehicleType: values?.VehicleType || "",
            StartDateTime: values?.StartDateTime?.toDate?.()?.toJSON?.(),
            ReturnDateTime: values?.ReturnDateTime?.toDate?.()?.toJSON?.(),
            PickUp: values?.PickUp || "",
            DropOff: values?.DropOff || "",
        }];

        try {
            await updateTripRq?.put(payload);
            setEditModal(false);
            setSuccessModal("Trip order updated successfully");
            refresh?.();
        } catch (e) {

        }
    }
    const _deleteTripOrder = async () => {
        try {
            await deleteTripRq?.post({});
            setExpanded(false);
            setDeleteModal(false);
            setSuccessModal("Trip order deleted successfully");
            refresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to delete trip order");
        }
    }

    const handleDocumentDownload = async (key, callback, fallbackMessage) => {
        if (documentLoadingKey) return;

        setDocumentLoadingKey(key);
        setDocumentDownloadProgress({ key, percent: 1, phase: "Starting download" });

        try {
            await callback(progress => setDocumentDownloadProgress({ key, ...progress }));
        } catch (e) {
            toast.error(e?.message || fallbackMessage);
        } finally {
            setDocumentLoadingKey(null);
            setTimeout(() => {
                setDocumentDownloadProgress(currentProgress =>
                    currentProgress?.key === key ? null : currentProgress
                );
            }, 900);
        }
    }
    const reassignedStatus = tripAssignHistories.data?.find?.(F => F.status === TripAssignStatuses.REASSIGNED_TO);
    const acceptedStatus = tripAssignHistories.data?.find?.(F => F.status === TripAssignStatuses.ACCEPTED_BY);
    const assignedDriverName = trip?.driver?.user?.name || driver?.user?.name || "";
    const tripReference = getTripReference(trip);
    const cancellationEntry = getCancellationEntry(trip);
    const cancellationDate = formatCancellationDate(cancellationEntry);
    const offerDeliverySummary = buildOfferDeliverySummary(tripOrderMessages?.list, tripOrderMessages?.loading);
    const offeredDriverLabel = buildSentToDriverLabel(tripOrderMessages?.list, trip);
    const tripStatusLabel =
        TRIP_STATUSES[trip?.status] +
        (trip?.status === TripStatus.Pending ? (assignedDriverName ? ` driver ${assignedDriverName}` : offeredDriverLabel) : "") +
        ((trip?.status === TripStatus.DriverAccepted || trip?.status === TripStatus.TripStarted) && assignedDriverName ? ` driver ${assignedDriverName}` : "");
    const trackingTripContext = {
        ...trip,
        driver: trip?.driver || driver,
        driverName: assignedDriverName,
    };

    // const CompletePdf = usePDF({ document: CompleteDoc({ inspection: preInspectRq?.data, postInspection: postInspectRq?.data, trip: trip, acceptedStatus: acceptedStatus }) })





    return <div className="border px-10 py-5 border-black border-solid flex flex-col rounded-[28px] bg-white">
        <SuccessModal open={successModal} onClose={setSuccessModal?.bind(this, false)} message={successModal} />
        <div id={`TRIP-CARD-${trip?.id}`} className={`border-b border-transparent ${expanded ? "border-b-[#e9dfd6] border-solid" : ""} gap-5 flex flex-col`}>
            <SectionBanner title="1 - Trip Order Overview" icon={<FileTextOutlined />} className="mt-2" />
            <div className="grid grid-cols-2 xl:grid-cols-7 py-5 gap-4 flex-1">
                <SummaryCell label="Client Name" value={trip?.client?.user?.name} />
                <SummaryCell label="MYC Reference Number" value={tripReference} />
                <SummaryCell label="Date" value={formatDateZA(trip?.startDateTime)} />
                <SummaryCell label="Trip Status" value={tripStatusLabel} valueClassName="text-green-600 font-medium" />
                <SummaryCell label="From Address" value={formatTripAddress(trip?.pickUp)} />
                <SummaryCell label="To Address" value={formatTripAddress(trip?.dropOff)} />
                <SummaryCell label="MYC Ref No" value={tripReference} mono />
                {trip?.status === TripStatus.Cancelled && <SummaryCell
                    label="Cancelled By"
                    value={`${formatCancellationUser(cancellationEntry)}${cancellationDate ? ` on ${cancellationDate}` : ""}`}
                    valueClassName="text-red-600 font-medium"
                />}
            </div>
            <div className="flex flex-row justify-end gap-3 pb-5">
                {canTrackVehicle && <Button onClick={() => navigate(`/track?tripId=${trip?.id}`)} className="min-w-[11rem]">
                    Track Vehicle
                </Button>}
                <Button onClick={setExpanded.bind(this, !expanded)} type="primary" className="min-w-[11rem]">View {expanded ? "Less" : "More"}</Button>
            </div>
        </div>
        {expanded && <>
            <div className="mt-6 rounded-[32px] border border-[#f1d3c5] bg-[linear-gradient(180deg,#fff7f3_0%,#ffffff_22%)] px-6 py-6 shadow-[0_26px_70px_rgba(236,88,43,0.10)]">
                <SectionBanner title="2 - Trip Order Details" icon={<SolutionOutlined />} />
                <div className="mt-6 flex flex-col gap-6">
                    <SectionCard>
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                            <SummaryCell label="Vehicle Reg No" value={trip?.vehicleRegNumber || trip?.VehicleRegNo} />
                            <SummaryCell label="Vehicle Type" value={trip?.vehicleType || trip?.VehicleType} />
                            <SummaryCell label="Amount excl VAT" value={`R${trip?.price?.toFixed?.(2) || "0.00"}`} valueClassName="text-green-600 font-medium" />
                            {process.env.REACT_APP_MODE === Constants.ADMIN ? <SummaryCell label="Rate/KM" value={`R${trip?.client?.pricePerKM || 0}`} /> : <div />}
                            <SummaryCell label="Start Date" value={formatDateZA(trip?.startDateTime)} />
                            <SummaryCell label={trip?.type === TripType.Return ? "Shuttle/Pilot" : "Start Time"} value={trip?.type === TripType.Return ? "Return Trip" : formatTimeZA(trip?.startDateTime)} valueClassName={trip?.type === TripType.Return ? "text-primary-orange font-medium" : ""} />
                            <SummaryCell label="Total KM" value={`${trip?.totalKM?.toFixed?.(2) || "0.00"} KM`} />
                        </div>
                    </SectionCard>
                    {canManageDriverWorkflow && !trip?.driver?.id && <DetailSection title="3 - Driver Onboarding" icon={<CarOutlined />}>
                        <div className="flex py-5 border border-[#ece5dd] rounded-[28px] bg-white shadow-[0_18px_55px_rgba(15,23,42,0.05)] px-8 flex-col gap-5 ">
                            <span className="self-start font-bold">Driver Trip Order Message Onboarding</span>
                            <Form onFinish={_driverMsg} form={driverMsgForm}>
                                <div className="grid grid-cols-8 gap-2">
                                    <span className="font-bold col-span-4">Assign Trip Order Message To Driver(s)</span>
                                    <span className="font-bold">All Driver in Region</span>
                                    <span className="font-bold text-center">Message Status</span>
                                    <span className="font-bold">Message Recipients</span>
                                    <div className="flex flex-row gap-3 col-span-4">
                                        <MYCSelect label="Region" name="originId" className="flex-1" required initalValue={isRegionalManager ? regionalDefaultOriginId : undefined}
                                            options={origins?.map?.(M => ({ label: M?.name, value: M?.id }))}
                                        />
                                        {!allDrivers && <MYCRemoteSearchSelect onlyActive required={!allDrivers} disabled={allDrivers} name="driverIds" multiple labelDef={d => d?.user?.name + " (" + d?.user?.email + ")"} label="Driver" url={driverSearchUrl} searchAttr={"driverName"} className="flex-1" />}
                                    </div>
                                    <Form.Item className="mx-auto" name="allDrivers" valuePropName="checked" label="">
                                        <Checkbox></Checkbox>
                                    </Form.Item>
                                    <div className="flex min-h-[2.75rem] items-center justify-center text-center">
                                        <span className={offerDeliverySummary.statusClassName}>{offerDeliverySummary.statusLabel}</span>
                                    </div>
                                    <div className="mx-auto flex min-h-[2.75rem] items-center gap-3">
                                        <span className="text-sm text-[#6b5b4d]">{offerDeliverySummary.recipientsLabel}</span>
                                        {offerDeliverySummary.canViewRecipients && (
                                            <img alt="" onClick={setRcvsModal.bind(this, true)} src={Assets.EyeSquare} className="w-7 h-7 object-contain cursor-pointer" />
                                        )}
                                    </div>
                                    <Button loading={driverMsgRq?.loading} htmlType={'submit'} type="primary" className="col-span-4">Send Trip Order To Driver(s)</Button>
                                </div>
                            </Form>
                        </div>
                    </DetailSection>}
                    {trip?.driver &&
                        <DetailSection title="3 - Driver Allocation" icon={<CarOutlined />}>
                            <SectionCard title="Driver Allocated">
                                <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                                    <SummaryCell label="Driver Name" value={trip?.driver?.user?.name} valueClassName="text-myc-blue font-medium" />
                                    <SummaryCell label="Time of Acceptance" value={formatTimeZA((acceptedStatus ?? reassignedStatus)?.createdAt)} />
                                    <SummaryCell label="Date of Acceptance" value={formatDateZA((acceptedStatus ?? reassignedStatus)?.createdAt)} />
                                    <SummaryCell label="Status" value="Accepted" valueClassName="text-green-500 font-medium" />
                                </div>
                            </SectionCard>
                        </DetailSection>
                    }
                    {canManageDriverWorkflow &&
                        <DetailSection title="4 - Driver Reassignment" icon={<SolutionOutlined />}>
                            <div className="flex py-5 border border-[#ece5dd] rounded-[28px] bg-white shadow-[0_18px_55px_rgba(15,23,42,0.05)] px-8 flex-col gap-5 ">
                                <span className="self-start font-bold">Driver Reassignment Log</span>
                                <div className="grid grid-cols-7 gap-3">
                                    <span className="col-span-3 font-bold">Select Driver to Reassign</span>
                                    <span className="font-bold">Continued From</span>
                                    <span className="font-bold">Time of Reassignment</span>
                                    <span className="font-bold">Date of Reassignment</span>

                                    <Form onFinish={_driverReassign} form={driverReassignForm} layout='vertical' className="col-span-3 flex flex-row gap-3 items-end">
                                        <MYCRemoteSearchSelect onlyActive name="driverId" labelDef={d => d?.user?.name + " (" + d?.user?.email + ")"} label="Driver" url={driverSearchUrl} searchAttr={"driverName"} className="flex-1" required />
                                        <Button loading={driverReRq?.loading} htmlType="submit" type="primary">Save</Button>
                                    </Form>
                                    <span>{reassignedStatus?.reassignedAt !== null ? TRIP_STATUSES[reassignedStatus?.reassignedAt] : "Not Reassigned Yet"}</span>
                                    <span className=""> {reassignedStatus ? formatTimeZA(reassignedStatus?.createdAt) : "Not Reassigned Yet"}</span>
                                    <span className=""> {reassignedStatus ? formatDateZA(reassignedStatus?.createdAt) : "Not Reassigned Yet"}</span>


                                </div>
                            </div>
                        </DetailSection>
                    }
                    <DetailSection title="5 - Client Messaging" icon={<MessageOutlined />}>
                        <SectionCard title="Client Auto Message Status">
                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                <SummaryCell label="Driver Acceptance Message" value={trip?.driverAcceprtanceMessage ? "Sent" : "Pending"} valueClassName={trip?.driverAcceprtanceMessage ? "text-green-500 font-medium" : "text-red-500 font-medium"} />
                                <SummaryCell label="Driver Started Trip Message" value={trip?.driverStartedTripMessage ? "Sent" : "Pending"} valueClassName={trip?.driverStartedTripMessage ? "text-green-500 font-medium" : "text-red-500 font-medium"} />
                                <SummaryCell label="Driver Completed Trip Message" value={trip?.tripCompletedMessage ? "Sent" : "Pending"} valueClassName={trip?.tripCompletedMessage ? "text-green-500 font-medium" : "text-red-500 font-medium"} />
                            </div>
                        </SectionCard>
                    </DetailSection>
                    {isAdminUser && <DetailSection title="6 - Tracking & Compliance" icon={<SafetyOutlined />}>
                        <TrackingComplianceSection
                            tripId={trip?.id}
                            expanded={expanded}
                            trip={trackingTripContext}
                            preInspection={preRq?.data}
                            tripStatusLabel={tripStatusLabel}
                        />
                    </DetailSection>}
                    {isAdminUser && trip?.status === TripStatus.Completed && <DetailSection title="6A - Compliance Evidence" icon={<FileTextOutlined />}>
                        <TripComplianceEvidenceActions
                            trip={trip}
                            driverName={assignedDriverName}
                            driverIdentifier={trip?.driver?.user?.email || driver?.user?.email || trip?.driverId || driver?.id || ''}
                        />
                    </DetailSection>}
                    {(isAdminUser || isRegionalManager) && <DetailSection title="6B - Fuel & Toll Slips" icon={<FileTextOutlined />}>
                        <SectionCard title="Uploaded Slips">
                            <TripSlipsPanel tripId={trip?.id} />
                        </SectionCard>
                    </DetailSection>}
                    <DetailSection title="7 - Trip Status" icon={<FileTextOutlined />}>
                        <SectionCard title="Trip Order Live Status">
                            <div className="flex flex-col gap-5">
                                <span className="font-bold">Trip Status <span className="text-red-500">{tripStatusLabel}
                                </span></span>
                                <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
                                    <SummaryCell label="Step 1" value="Order Accepted" valueClassName={trip?.status === TripStatus.DriverAccepted ? "text-red-500 font-medium" : "text-[#4a4138]"} />
                                    <SummaryCell label="Step 2" value="Pre inspection" valueClassName={trip?.status === TripStatus.PreInspection ? "text-red-500 font-medium" : "text-[#4a4138]"} />
                                    <SummaryCell label="Step 3" value="On Route" valueClassName={trip?.status === TripStatus.TripStarted ? "text-red-500 font-medium" : "text-[#4a4138]"} />
                                    <SummaryCell label="Step 4" value="Post inspection" valueClassName={trip?.status === TripStatus.PostInspection ? "text-red-500 font-medium" : "text-[#4a4138]"} />
                                    <SummaryCell label="Step 5" value="Order Completed" valueClassName={trip?.status === TripStatus.Completed ? "text-red-500 font-medium" : "text-[#4a4138]"} />
                                </div>
                                <div className="flex flex-row justify-end">
                                    {canCancelTrip && <Button onClick={setCancelModal.bind(this, true)} type="primary" className="px-5">Cancel Ride</Button>}
                                </div>
                            </div>
                        </SectionCard>
                    </DetailSection>
                    <div className="flex flex-col gap-5 py-5">
                        <div className="flex flex-row gap-8">
                            {trip?.status !== TripStatus.Cancelled && <>
                                <InspectionButton rq={preRq} trip={trackingTripContext} post={false} />
                                <InspectionButton rq={postRq} trip={trackingTripContext} post={true} />
                            </>}
                            {trip?.status !== TripStatus.Cancelled && <>
                                <CompareButton rq={preRq} postRq={postRq} trip={trackingTripContext} post={false} />
                            </>}
                            {(trip?.status === TripStatus.Cancelled) ? <div className="flex flex-col gap-5">
                                <span className="font-bold">Cancellation Reason</span>
                                <div className="flex flex-row gap-5">
                                    <img alt="" src={Assets.EyeSquare} onClick={setCancelModal.bind(this, true)} className="cursor-pointer w-6 h-6" />
                                    {documentLoadingKey === 'cancel'
                                        ? <ActionLoadingIcon />
                                        : <img
                                            alt=""
                                            src={Assets.DownloadSquare}
                                            onClick={() => handleDocumentDownload(
                                                'cancel',
                                                onProgress => generateCancelDocument({
                                                    data: { trip, acceptedStatus: acceptedStatus ?? reassignedStatus },
                                                    fileName: `MYC-${trip?.id}-cancel-reason.pdf`,
                                                    onProgress,
                                                }),
                                                'Failed to generate cancellation PDF'
                                            )}
                                            className="cursor-pointer w-6 h-6"
                                        />}
                                </div>
                                <DownloadProgressBar progress={documentDownloadProgress?.key === 'cancel' ? documentDownloadProgress : null} />
                            </div> : <div />
                            }

                            {trip?.status !== TripStatus.Cancelled && <>

                                <div className="flex flex-col gap-5">
                                    <span className="font-bold">Trip Order</span>

                                    {documentLoadingKey === 'trip-order'
                                        ? <ActionLoadingIcon />
                                        : <img
                                            alt=""
                                            src={Assets.DownloadSquare}
                                            onClick={() => handleDocumentDownload(
                                                'trip-order',
                                                onProgress => generateTripOrderDocument({
                                                    data: { tripOrder: trip, acceptedStatus: acceptedStatus ?? reassignedStatus },
                                                    fileName: `MYC-${trip?.id}-trip-order.pdf`,
                                                    onProgress,
                                                }),
                                                'Failed to generate trip order PDF'
                                            )}
                                            className="cursor-pointer w-6 h-6"
                                        />}
                                    <DownloadProgressBar progress={documentDownloadProgress?.key === 'trip-order' ? documentDownloadProgress : null} />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <span className="font-bold">Complete Trip Order</span>
                                    {/* <PDFDownloadLink fileName={`MYC-${trip?.id}-` + "complete.pdf"} document={<CompleteDoc trip={trip} acceptedStatus={acceptedStatus ?? reassignedStatus} inspection={preRq} postInspection={postRq} />}>
                                {({ loading, url }) => {
                                    return <div className="flex flex-row gap-2">
                                        {loading ? <ActionLoadingIcon /> : <img alt="" src={Assets.DownloadSquare} className="w-6 h-6" />
                                        }</div>
                                }}
                            </PDFDownloadLink> */}
                                    <div className="flex flex-row gap-2">
                                        {documentLoadingKey === 'complete'
                                            ? <ActionLoadingIcon />
                                            : <img
                                                alt=""
                                                src={Assets.DownloadSquare}
                                                onClick={() => handleDocumentDownload(
                                                    'complete',
                                                    onProgress => generatePdfDocument({
                                                        data: { trip, acceptedStatus: acceptedStatus ?? reassignedStatus, inspection: preRq, postInspection: postRq },
                                                        fileName: `MYC-${trip?.id}-complete.pdf`,
                                                        onProgress,
                                                    }),
                                                    'Failed to generate complete trip order PDF'
                                                )}
                                                className="cursor-pointer w-6 h-6"
                                            />}
                                    </div>
                                    <DownloadProgressBar progress={documentDownloadProgress?.key === 'complete' ? documentDownloadProgress : null} />
                                </div>
                            </>}
                        </div>
                    </div>
                    {canUseAdminOnlyTripActions && <DetailSection title="8 - Admin Actions" icon={<SolutionOutlined />}>
                        <SectionCard title="Trip Order Management">
                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                <span className="text-sm text-[#6b5b4d]">
                                    Edit the trip details if there is a mistake, or permanently delete the trip order from Firebase.
                                </span>
                                <div className="flex flex-col gap-3 md:flex-row">
                                    <Button onClick={setEditModal.bind(this, true)} className="min-w-[11rem]">
                                        Edit Trip Order
                                    </Button>
                                    <Button danger onClick={setDeleteModal.bind(this, true)} className="min-w-[11rem]">
                                        Delete Trip Order
                                    </Button>
                                </div>
                            </div>
                        </SectionCard>
                    </DetailSection>}
                </div>
            </div>
        </>}
        <TripEditModal
            open={editModal}
            onCancel={setEditModal.bind(this, false)}
            onSubmit={_editTripOrder}
            loading={updateTripRq?.loading}
            trip={trip}
        />
        {/* <InspectionModal open={postInspect} tripOrderId={trip?.id} postInspect title="Post inspection Checklist" onCancel={setPostInspect.bind(this, false)} /> */}
        <Modal open={cancelModal} onCancel={setCancelModal.bind(this, false)} footer={null} title="Cancel Trip">
            {trip?.status === TripStatus.Cancelled ? <div className="flex flex-col gap-5">
                <span>{cancellationEntry?.reason}</span>
                <span className="text-sm text-[#6b5b4d]">
                    Cancelled by {formatCancellationUser(cancellationEntry)}
                    {cancellationDate ? ` on ${cancellationDate}` : ""}
                </span>
                {cancellationEntry?.image && <img alt="" src={getResourceUrl(cancellationEntry?.image)} className="object-contain w-64 h-64 self-center" />}

            </div> :
                <Form form={cancelForm} onFinish={_cancelTripOrder} layout="vertical">
                    <div className="flex flex-col gap-5">
                        <MYCTextArea name="description" label="Cancel Reason" required />
                        <Button htmlType="submit" loading={cancelRq?.loading} type="primary" className="self-end">Submit</Button>
                    </div>
                </Form>}
        </Modal>
        <Modal open={deleteModal} onCancel={setDeleteModal.bind(this, false)} footer={null} title={`Delete Trip Order MYC#${trip?.id}`}>
            <div className="flex flex-col gap-5">
                <span className="text-[#6b5b4d]">
                    This will permanently remove the trip order and its linked admin history from Firebase. This cannot be undone.
                </span>
                <div className="flex justify-end gap-3">
                    <Button onClick={setDeleteModal.bind(this, false)}>Cancel</Button>
                    <Button danger type="primary" loading={deleteTripRq?.loading} onClick={_deleteTripOrder}>
                        Delete Trip Order
                    </Button>
                </div>
            </div>
        </Modal>
        <Modal open={rcvsModal} onCancel={setRcvsModal.bind(this, false)} footer={null} title="Message Recipients">
            <MYCTable
                {...tripOrderMessages}
                cols={[
                    {
                        id: "created",
                        dataIndex: "createdAt",
                        render: text => formatDateTimeZA(text),
                        title: "Sent At"
                    },
                    {
                        id: "rcv",
                        dataIndex: "driverId",
                        render: (text, record) => {
                            if (record?.driverId) {
                                return record?.driver?.user?.name;
                            }
                            return "All Drivers In Region -  " + record?.origin?.name;
                        },
                        title: "Sent To"
                    },
                ]}
            />
        </Modal>
    </div>
}
